import { useMemo, useState } from 'react'
import { Input } from '@heroui/react'
import { nameIn } from '../../lib/staffText'
import { putQueue } from '../../lib/profilesDoc'
import type { OperationDoc, ProfileProblem, ProfileQueue, ProfileSegment, SegmentAppointment, Service } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { LANGUAGES } from '../parts'
import { Confirm } from '../sectionKit'
import { Chips, NumInput, ProfileModal, Segmented, cardProblems, queueOptions, useLiveProblems, useProblemSlots } from './sheetParts'
import { Pick, Tick } from '../setupKit'
import { QUEUE_FIELDS, QueueFields } from './QueueSheet'

const PRIORITIES = ['standard', 'priority', 'vip'] as const
const SOURCES = ['kiosk', 'reception', 'counter', 'mobile', 'appointment'] as const
const BOOKING: readonly SegmentAppointment[] = ['', 'yes', 'no']

type ListKey = 'serviceIds' | 'priorities' | 'languages' | 'sources'

function withSegment(doc: OperationDoc, groupId: string, seg: ProfileSegment): OperationDoc {
  return {
    ...doc,
    groups: doc.groups.map((g) =>
      g.id === groupId ? { ...g, segments: g.segments.map((s) => (s.id === seg.id ? seg : s)) } : g),
  }
}

export function SegmentSheet({
  doc,
  groupId,
  segment,
  freshQueue,
  services,
  problems,
  readOnly,
  onApply,
  onRemove,
  onClose,
}: {
  doc: OperationDoc
  groupId: string
  segment: ProfileSegment
  /** The queue made with a new rule (Qmatic's "rule with queue"), while it is still the rule's alone. */
  freshQueue?: ProfileQueue
  services: Service[]
  problems: ProfileProblem[]
  readOnly: boolean
  onApply: (s: ProfileSegment, q?: ProfileQueue) => void
  onRemove: () => void
  onClose: () => void
}) {
  const { lang, say } = useStaffText()
  const [draft, setDraft] = useState<ProfileSegment>(segment)
  const [queue, setQueue] = useState(freshQueue)
  const ownQueue = queue && draft.queueId === queue.id ? queue : undefined
  const group = doc.groups.find((g) => g.id === groupId)

  const trial = useMemo(() => {
    const d = withSegment(doc, groupId, draft)
    return queue ? putQueue(d, queue) : d
  }, [doc, groupId, draft, queue])
  const live = useLiveProblems(trial)
  const slots = useProblemSlots(cardProblems(live, 'segment', segment.id, problems), [
    'name', 'serviceIds', 'priorities', 'languages', 'sources', 'appointment', 'lateOverMin', 'queueId',
  ])
  const queueSlots = useProblemSlots(ownQueue ? cardProblems(live, 'queue', ownQueue.id, []) : [], QUEUE_FIELDS)

  // An empty list is absent: none ticked means any.
  const toggle = (key: ListKey, v: string) =>
    setDraft((d) => {
      const had = d[key] ?? []
      const list = had.includes(v) ? had.filter((x) => x !== v) : [...had, v]
      const next = { ...d }
      if (list.length > 0) next[key] = list
      else delete next[key]
      return next
    })

  const booking = draft.appointment ?? ''
  const setBooking = (b: SegmentAppointment) =>
    setDraft((d) => {
      const next = { ...d }
      if (b === '') delete next.appointment
      else next.appointment = b
      if (b !== 'yes') {
        delete next.lateOverMin
        // An appointment queue takes only booked visits: fall back to the group's default.
        const q = trial.queues.find((x) => x.id === d.queueId)
        if (q?.type === 'appointment' && group) next.queueId = group.defaultQueueId
      }
      return next
    })
  const setLate = (n: number | undefined) =>
    setDraft((d) => {
      const next = { ...d }
      if (n === undefined) delete next.lateOverMin
      else next.lateOverMin = n
      return next
    })

  const serviceName = (id: string) => {
    const s = services.find((x) => x.id === id)
    return s ? nameIn(s.name, lang, s.code) : say('profiles.sheet.serviceGone')
  }
  const groupServices = group?.serviceIds ?? []
  const serviceOpts = [...groupServices, ...(draft.serviceIds ?? []).filter((id) => !groupServices.includes(id))].map(
    (id) => [id, serviceName(id)] as const,
  )
  const langOpts = [
    ...LANGUAGES,
    ...(draft.languages ?? []).filter((l) => !LANGUAGES.some(([c]) => c === l)).map((l) => [l, l] as [string, string]),
  ]
  const queues = queueOptions(
    trial.queues.filter((q) => q.type === 'normal' || booking === 'yes' || q.id === draft.queueId),
    lang,
  )

  return (
    <ProfileModal
      title={draft.name.trim() || say('profiles.segment')}
      readOnly={readOnly}
      onClose={onClose}
      onDone={() => onApply({ ...draft, name: draft.name.trim() }, ownQueue)}
      extra={
        !readOnly && (
          <Confirm
            ask={say('profiles.seg.remove')}
            question={say('profiles.seg.removeAsk')}
            yes={say('profiles.sheet.remove')}
            keep={say('profiles.sheet.keep')}
            onYes={onRemove}
          />
        )
      }
    >
      {slots.head}
      <fieldset className="pf-sheet" disabled={readOnly}>
        <label className="field">
          <span>{say('profiles.sheet.name')}</span>
          <Input
            fullWidth
            autoFocus
            value={draft.name}
            aria-invalid={slots.has('name') || undefined}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </label>
        {slots.at('name')}

        <fieldset className="field">
          <legend title={say('profiles.seg.emptyAny')}>{say('profiles.sheet.services')}</legend>
          {serviceOpts.length === 0 ? (
            <small>{say('profiles.seg.noServices')}</small>
          ) : (
            <div className="setup-ticks">
              {serviceOpts.map(([id, label]) => (
                <Tick key={id} checked={draft.serviceIds?.includes(id) ?? false} disabled={readOnly} onChange={() => toggle('serviceIds', id)}>
                  <span className={groupServices.includes(id) ? undefined : 'pf-gone'}>{label}</span>
                </Tick>
              ))}
            </div>
          )}
        </fieldset>
        {slots.at('serviceIds')}

        <fieldset className="field">
          <legend>{say('profiles.seg.priority')}</legend>
          <Chips
            label={say('profiles.seg.priority')}
            options={PRIORITIES.map((p) => [p, say(`profiles.seg.p.${p}`)] as const)}
            value={draft.priorities ?? []}
            disabled={readOnly}
            onToggle={(v) => toggle('priorities', v)}
          />
        </fieldset>
        {slots.at('priorities')}

        <fieldset className="field">
          <legend>{say('profiles.seg.languages')}</legend>
          <Chips
            label={say('profiles.seg.languages')}
            options={langOpts}
            value={draft.languages ?? []}
            disabled={readOnly}
            onToggle={(v) => toggle('languages', v)}
          />
        </fieldset>
        {slots.at('languages')}

        <fieldset className="field">
          <legend>{say('profiles.seg.source')}</legend>
          <Chips
            label={say('profiles.seg.source')}
            options={SOURCES.map((s) => [s, say(`profiles.seg.src.${s}`)] as const)}
            value={draft.sources ?? []}
            disabled={readOnly}
            onToggle={(v) => toggle('sources', v)}
          />
        </fieldset>
        {slots.at('sources')}

        <fieldset className="field">
          <legend>{say('profiles.seg.booking')}</legend>
          <Segmented
            label={say('profiles.seg.booking')}
            options={BOOKING.map((b) => [b, say(`profiles.seg.b.${b || 'any'}`)] as const)}
            value={booking}
            disabled={readOnly}
            onChange={setBooking}
          />
        </fieldset>
        {slots.at('appointment')}

        {(booking === 'yes' || draft.lateOverMin !== undefined) && (
          <>
            <label className="field">
              <span title={say('profiles.seg.lateOff')}>{say('profiles.seg.lateOver')}</span>
              <NumInput value={draft.lateOverMin} min={0} max={240} invalid={slots.has('lateOverMin')} onChange={setLate} />
            </label>
            {slots.at('lateOverMin')}
          </>
        )}

        <label className="field">
          <span>{say('profiles.queue')}</span>
          <Pick
            label={say('profiles.queue')}
            value={draft.queueId}
            onChange={(queueId) => setDraft((d) => ({ ...d, queueId }))}
            options={queues}
            disabled={readOnly}
          />
        </label>
        {slots.at('queueId')}
      </fieldset>

      {ownQueue && (
        <section className="pf-modal__part" aria-label={say('profiles.queue')}>
          {queueSlots.head}
          <QueueFields draft={ownQueue} set={(patch) => setQueue((q) => (q ? { ...q, ...patch } : q))} slots={queueSlots} readOnly={readOnly} compact />
        </section>
      )}
    </ProfileModal>
  )
}
